import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import gtssLogo from '../../../assests/images/gtss_logo.jpeg';
import { Images } from '../../../assests/images/Images';
import { useLoginMutation } from '../../../mutations/authMutations';
import queryClient from '../../../lib/queryClient';
import ForgotPasswordModal from '../../../components/popupmodal/ForgotPasswordModal';

const Login = ({ onLogin }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [isImporter, setIsImporter] = useState(false);
  const [isForgotModalOpen, setIsForgotModalOpen] = useState(false);
  const loginMutation = useLoginMutation();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const onSubmit = (data) => {
    const payload = isImporter
      ? { email: data.email, password: data.password }
      : { emailId: data.email, password: data.password };

    loginMutation.mutate(
      { isImporter, ...payload },
      {
        onSuccess: (response) => {
          const token =
            typeof response === 'string'
              ? response
              : response?.token ??
                response?.accessToken ??
                response?.data?.token ??
                response?.data?.accessToken ??
                response?.jwtToken;

          if (token && typeof token === 'string') {
            localStorage.setItem('authToken', token);
          }
          sessionStorage.setItem('isLoggedIn', 'true');
          sessionStorage.setItem('userType', isImporter ? 'importer' : 'system');
          if (isImporter) {
            sessionStorage.setItem('importer', 'true');
          }
          // Store role for role-based sidebar visibility
          const role = response?.role ?? response?.data?.role ?? '';
          if (role) {
            sessionStorage.setItem('userRole', role);
          }
          // Store display name & email for the profile card in the sidebar
          if (isImporter) {
            // Importer login: response has customerName
            const name = response?.customerName ?? response?.data?.customerName ?? 'Importer';
            sessionStorage.setItem('userName', name);
            sessionStorage.setItem('userEmail', '');
          } else {
            // Normal login: response has firstName, lastName, emailId
            const first = response?.firstName ?? response?.data?.firstName ?? '';
            const last  = response?.lastName  ?? response?.data?.lastName  ?? '';
            const email = response?.emailId   ?? response?.data?.emailId   ?? '';
            sessionStorage.setItem('userName', `${first} ${last}`.trim() || email);
            sessionStorage.setItem('userEmail', email);
          }

          // Store P&L password from login response (used to gate P&L report access)
          const pandlPwd =
            response?.pandlPassword ??
            response?.data?.pandlPassword ??
            '';
          if (pandlPwd) {
            sessionStorage.setItem('pandlPassword', pandlPwd);
          } else {
            sessionStorage.removeItem('pandlPassword');
          }

          // Clear ALL React Query cache so previous session's data
          // (from a different role/user) never shows for this new login.
          queryClient.clear();
          if (onLogin) onLogin();
        },
      }
    );
  };

  return (
    <div className="lp-wrapper" style={{ backgroundImage: `url(${Images.globalbg})` }}>
      <div className="container-fluid min-vh-100 d-flex align-items-center justify-content-center py-4 px-2 px-sm-3">
        <div className="w-100" style={{ maxWidth: '460px' }}>
          <div className="lp-card">

            {/* Logo */}
            <div className="text-center mb-3">
              <img src={gtssLogo} alt="GTSS logo" className="lp-logo" />
            </div>

            <h1 className="lp-heading">Sign In</h1>
              <p className="lp-subtitle">Welcome Back</p>

              <form onSubmit={handleSubmit(onSubmit)} noValidate autoComplete="off">

                {/* Email */}
                <div className="mb-3">
                  <div className={`lp-input-group${errors.email ? ' lp-input-group--error' : ''}`}>
                    <span className="lp-input-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M0 4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V4Zm2-1a1 1 0 0 0-1 1v.217l7 4.2 7-4.2V4a1 1 0 0 0-1-1H2Zm13 2.383-4.708 2.825L15 11.105V5.383Zm-.034 6.876-5.64-3.471L8 9.583l-1.326-.795-5.64 3.47A1 1 0 0 0 2 13h12a1 1 0 0 0 .966-.741ZM1 11.105l4.708-2.897L1 5.383v5.722Z" />
                      </svg>
                    </span>
                    <input
                      id="email"
                      type="email"
                      className="lp-input form-control"
                      placeholder="Email address"
                      autoComplete="off"
                      {...register('email', {
                        required: 'Email address is required.',
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: 'Please enter a valid email address.',
                        },
                      })}
                    />
                  </div>
                  {errors.email && (
                    <span className="lp-error" role="alert">{errors.email.message}</span>
                  )}
                </div>

                {/* Password */}
                <div className="mb-2">
                  <div className={`lp-input-group${errors.password ? ' lp-input-group--error' : ''}`}>
                    <span className="lp-input-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z" />
                      </svg>
                    </span>
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      className="lp-input form-control"
                      placeholder="Password"
                      autoComplete="off"
                      {...register('password', { required: 'Password is required.' })}
                    />
                    <button
                      type="button"
                      className="lp-toggle-btn"
                      onClick={() => setShowPassword((p) => !p)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
                          <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
                          <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.708zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                          <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
                          <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <span className="lp-error" role="alert">{errors.password.message}</span>
                  )}
                </div>

                {/* Importer Checkbox & Forgot Password Row */}
                <div className="lp-importer-container">
                  <div className="lp-importer-checkbox-wrap">
                    <input
                      type="checkbox"
                      id="importer-checkbox"
                      className="lp-importer-checkbox"
                      checked={isImporter}
                      onChange={(e) => setIsImporter(e.target.checked)}
                    />
                    <label htmlFor="importer-checkbox" className="lp-importer-label">
                      Login as Importer
                    </label>
                  </div>

                  <button
                    type="button"
                    className="lp-forgot btn btn-link p-0"
                    onClick={() => setIsForgotModalOpen(true)}
                  >
                    Forgot Password?
                  </button>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  id="login-submit-btn"
                  className="lp-submit-btn w-100"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </button>

              </form>
            </div>
          </div>
        </div>

      {/* ── Forgot Password Bootstrap 5 Modal ── */}
      <ForgotPasswordModal
        isOpen={isForgotModalOpen}
        onClose={() => setIsForgotModalOpen(false)}
        initialEmail={watch('email') || ''}
        isImporter={isImporter}
      />
    </div>
  );
};

export default Login;